import { authState, authActions } from '../stores/authStore';

const API_BASE = '/api';

// Track whether a refresh is already in progress to avoid duplicate calls
let refreshPromise: Promise<string | null> | null = null;

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authState.accessToken) {
    headers['Authorization'] = `Bearer ${authState.accessToken}`;
  }
  return headers;
}

async function handleResponse(res: Response) {
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(error.error || error.message || `HTTP ${res.status}`) as Error & {
      status: number;
    };
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Perform a fetch with auth headers. On 401, attempt a single token
 * refresh and retry the request. If the refresh also fails, redirect
 * to the login page.
 */
async function authFetch(path: string, options: RequestInit = {}): Promise<unknown> {
  const url = `${API_BASE}${path}`;

  const res = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options.headers as Record<string, string>) },
  });

  if (res.status === 401 && authState.accessToken) {
    // Access token expired — try refreshing
    if (!refreshPromise) {
      refreshPromise = authActions.refreshAccessToken();
    }
    const newToken = await refreshPromise;
    refreshPromise = null;

    if (newToken) {
      // Retry the original request with the new token
      const retryRes = await fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...(options.headers as Record<string, string>),
          Authorization: `Bearer ${newToken}`,
        },
      });
      return handleResponse(retryRes);
    }

    // Refresh failed — redirect to login
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  return handleResponse(res);
}

export const api = {
  get: (path: string) => authFetch(path),

  post: (path: string, body: unknown) =>
    authFetch(path, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  put: (path: string, body: unknown) =>
    authFetch(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  del: (path: string) => authFetch(path, { method: 'DELETE' }),
};
